// packages/apps-script/src/select-and-edit-utils.ts

interface BoundingRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ComponentEventData {
  filename: string;
  element: string; // tagName
  locator: string;
  componentNickname: string;
  boundingRect: BoundingRect;
}

interface DebugLocatorInfo {
  filename: string;
  element: string; // tagName
  locator: string;
}

document.addEventListener("DOMContentLoaded", () => {
  const scanElements = () => {
    const elementsWithLocator = Array.from(
      document.querySelectorAll<HTMLElement>("[data-locator]"),
    );

    const locatorData: DebugLocatorInfo[] = elementsWithLocator.map((el) => {
      const locator = el.getAttribute("data-locator") || "";
      const parts = locator.split(":");

      return {
        element: el.tagName.toLowerCase(),
        filename: parts[0] || "unknown",
        locator: locator,
      };
    });

    // console.log(`Found ${locatorData.length} elements with data-locator attributes`, locatorData);

    // Send data to parent window if in an iframe
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          data: locatorData,
          type: "DEBUG_LOCATORS",
        },
        "*", // Consider making targetOrigin configurable if needed in the future
      );
    }
  };

  const getComponentNickname = (element: HTMLElement): string => {
    if (element.innerText && element.innerText.trim()) {
      return element.innerText.trim();
    }

    const findInnerText = (elem: Element): string | null => {
      if (elem.childNodes) {
        for (const node of Array.from(elem.childNodes)) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            return node.textContent.trim();
          }
        }
      }

      if (elem.children) {
        for (const child of Array.from(elem.children)) {
          const text = findInnerText(child);

          if (text) {
            return text;
          }
        }
      }

      return null;
    };

    return findInnerText(element) || "";
  };

  // Listen for messages from the parent window
  window.addEventListener("message", (event: MessageEvent) => {
    if (event.data && event.data.type) {
      // console.log("Received message from parent:", event.data);
      // Handle any parent messages here if needed
    }
  });

  // This listener logs messages *sent* by this window to its parent.
  // It was in the original script.
  window.addEventListener("message", (event: MessageEvent) => {
    // Log all messages for debugging if they originated from this window
    if (event.source === window) {
      // console.log("Message sent to parent:", event.data);
    }
  });

  // Set up mouse hover detection
  document.addEventListener("mouseover", (event: MouseEvent) => {
    const element = event.target as HTMLElement | null;

    if (element && typeof element.getAttribute === "function") {
      // Ensure element is an HTMLElement
      const locator = element.getAttribute("data-locator");

      if (locator) {
        const parts = locator.split(":");
        const rect = element.getBoundingClientRect();
        const componentNickname = getComponentNickname(element);

        const data: ComponentEventData = {
          boundingRect: {
            height: rect.height,
            width: rect.width,
            x: rect.x,
            y: rect.y,
          },
          componentNickname: componentNickname,
          element: element.tagName.toLowerCase(),
          filename: parts[0] || "unknown",
          locator: locator,
        };

        // console.log("Hovered element with locator:", data);
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({data: data, type: "COMPONENT_HOVER"}, "*");
        }
      }
    }
  });

  // Set up mouse out detection
  document.addEventListener("mouseout", (event: MouseEvent) => {
    const element = event.target as HTMLElement | null;

    if (element && typeof element.getAttribute === "function") {
      // Ensure element is an HTMLElement
      const locator = element.getAttribute("data-locator");

      if (locator) {
        const parts = locator.split(":");
        const rect = element.getBoundingClientRect();
        const componentNickname = getComponentNickname(element);
        const data: ComponentEventData = {
          boundingRect: {
            height: rect.height,
            width: rect.width,
            x: rect.x,
            y: rect.y,
          },
          componentNickname: componentNickname,
          element: element.tagName.toLowerCase(),
          filename: parts[0] || "unknown",
          locator: locator,
        };

        // console.log("Mouse out from element with locator:", data);
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({data: data, type: "COMPONENT_MOUSE_OUT"}, "*");
        }
      }
    }
  });

  // Set up click detection
  document.addEventListener(
    "click",
    (event: MouseEvent) => {
      const element = event.target as HTMLElement | null;

      if (element) {
        const closestWithLocator = element.closest<HTMLElement>("[data-locator]");

        if (closestWithLocator) {
          const locator = closestWithLocator.getAttribute("data-locator")!; // Known to exist due to closest
          const parts = locator.split(":");
          const rect = closestWithLocator.getBoundingClientRect();
          const componentNickname = getComponentNickname(closestWithLocator);
          const data: ComponentEventData = {
            boundingRect: {
              height: rect.height,
              width: rect.width,
              x: rect.x,
              y: rect.y,
            },
            componentNickname: componentNickname,
            element: closestWithLocator.tagName.toLowerCase(),
            filename: parts[0] || "unknown",
            locator: locator,
          };

          // console.log("Clicked element with locator:", data);
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({data: data, type: "COMPONENT_CLICK"}, "*");
          }
        }
      }
    },
    true, // Use capture phase
  );

  // Run initial scan
  scanElements();

  // Set up a periodic scan (every 1 second)
  // Consider if 1s is too frequent for production or make it configurable.
  setInterval(scanElements, 1000);
});

// Adding an empty export to ensure TypeScript treats this file as a module.
// This is helpful for `tsup` and type declaration generation.
export {};
